import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { logHardwareDatadogEvent } from '@/utils/analytics/log-hardware-datadog-event';
import { stripDeviceFileNameForAnalytics } from '@/utils/analytics/strip-device-file-name';

export interface TrackDeviceConnectedParams {
  deviceId: string | null;
  serialNumber: string | null;
  prevStatus: string | null;
  firmwareVersion?: string;
}

export interface TrackDeviceDisconnectedParams {
  deviceId: string | null;
  serialNumber: string | null;
  reason: 'manual' | 'unexpected' | 'poweroff_batlow';
}

// A crash-looping device emits one hardware_device_disconnected per bounce
// (~28/hour observed), drowning the metric in repeats of one failure. Group
// bounces per device into episodes: only the first unexpected disconnect of
// an episode emits; manual disconnects are user actions and always emit.
const EPISODE_QUIET_MS = 5 * 60 * 1000;

let lastUnexpectedDisconnect: { deviceId: string | null; at: number } | null = null;

/** Test-only: reset the disconnect-episode grouping state. */
export function resetDisconnectEpisodeTrackingForTests(): void {
  lastUnexpectedDisconnect = null;
}

/**
 * Track device connected event (T047).
 *
 * `deviceId` = backend MongoDB _id (from linkedDeviceId).
 * `serialNumber` = hardware serial extracted from device name.
 */
export function trackDeviceConnected({
  deviceId,
  serialNumber,
  prevStatus,
  firmwareVersion,
}: TrackDeviceConnectedParams): void {
  const isReconnection = prevStatus !== 'scanning' && prevStatus !== null;
  const isAutoReconnect = prevStatus === 'reconnecting';

  track({
    event_name: TrackingEventName.HardwareDeviceConnected,
    props: {
      device_id: deviceId ?? undefined,
      serial_number: serialNumber ?? undefined,
      is_reconnection: isReconnection,
      is_auto_reconnect: isAutoReconnect,
      // Rust DTO now sends `null` instead of "unknown" for missing firmware,
      // but the link request path in useChroniclePairing still falls back to
      // "unknown" — keep this guard defensively.
      firmware_version:
        firmwareVersion && firmwareVersion !== 'unknown' ? firmwareVersion : undefined,
    },
    // APP-6285: also send to PostHog (was Datadog/Snowplow/Amplitude only).
    options: { posthog: true },
  });
}

/** Datadog-only: with sync-async ON a BLE drop holds the session open instead
 *  of stopping it, so the interrupted stop path never marks it — without this
 *  the dashboard reads a hybrid (interrupted) session as pure online. */
export function logRecordingInterruptedHold({
  sessionId,
  fileName,
}: {
  sessionId: string | null;
  fileName: string | null;
}): void {
  logHardwareDatadogEvent(
    'hardware_recording_interrupted',
    {
      session_id: sessionId,
      session_id_local: sessionId,
      device_file_name: stripDeviceFileNameForAnalytics(fileName),
      reason: 'ble_disconnect',
      recovery_mode: 'live_offline_hold',
    },
    'warn'
  );
}

/**
 * Track device disconnected event (T048).
 *
 * `deviceId` = backend MongoDB _id (from linkedDeviceId).
 * `serialNumber` = hardware serial extracted from device name.
 */
export function trackDeviceDisconnected({
  deviceId,
  serialNumber,
  reason,
}: TrackDeviceDisconnectedParams): void {
  if (reason === 'unexpected') {
    const now = Date.now();
    const last = lastUnexpectedDisconnect;
    lastUnexpectedDisconnect = { deviceId, at: now };
    if (last !== null && last.deviceId === deviceId && now - last.at < EPISODE_QUIET_MS) {
      // Same episode, already reported — each bounce still lands in logs via
      // the provider's unexpected-disconnect warn.
      return;
    }
  }

  track({
    event_name: TrackingEventName.HardwareDeviceDisconnected,
    props: {
      device_id: deviceId ?? undefined,
      serial_number: serialNumber ?? undefined,
      disconnect_reason: reason,
    },
    // APP-6285: also send to PostHog (was Datadog/Snowplow/Amplitude only).
    options: { posthog: true },
  });
  logHardwareDatadogEvent(
    TrackingEventName.HardwareDeviceDisconnected,
    {
      device_id: deviceId,
      serial_number: serialNumber,
      disconnect_reason: reason,
    },
    reason === 'unexpected' ? 'warn' : 'info'
  );
}

export interface LogDevicePoweroffBatlowParams {
  deviceId: string;
  serialNumber: string | null;
  wasRecording: boolean;
  prevStatus: string | null;
  newStatus: string;
}

/** Datadog-only: deliberate low-battery shutdown — not a link failure. */
export function logDevicePoweroffBatlow({
  deviceId,
  serialNumber,
  wasRecording,
  prevStatus,
  newStatus,
}: LogDevicePoweroffBatlowParams): void {
  logHardwareDatadogEvent(
    'hardware_device_poweroff',
    {
      reason: 'batlow',
      was_recording: wasRecording,
      device_id: deviceId,
      serial_number: serialNumber,
      prev_status: prevStatus,
      new_status: newStatus,
    },
    'warn'
  );
}
